import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import { FiEye, FiEyeOff, FiMail, FiLock, FiUser, FiBriefcase, FiUsers } from 'react-icons/fi';

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState('business');
  const navigate = useNavigate();
  const { register: registerUser, isAuthenticated } = useAuth();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm();

  const password = watch('password');

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (data) => {
    // Only business signup is supported. Clients should use the client login.
    setLoading(true);
    // Shape businessInfo from form fields if provided
    const businessInfo = data.businessName ? {
      businessName: data.businessName,
      businessEmail: data.businessEmail || data.email,
      businessPhone: data.businessPhone,
      taxId: data.taxId,
      website: data.website,
      businessAddress: {
        street: data.businessStreet,
        city: data.businessCity,
        state: data.businessState,
        zipCode: data.businessZip,
        country: data.businessCountry,
      }
    } : undefined;
    const result = await registerUser({ ...data, userType, businessInfo });
    setLoading(false);
    if (result.success) {
      // Navigate handled by <Navigate /> above
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Create Account
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Start managing your invoices today
          </p>
        </div>
        
        <form className="mt-8 space-y-6 bg-white p-8 rounded-xl shadow-lg" onSubmit={handleSubmit(onSubmit)}>
          {/* Only Business accounts can sign up here */}

          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="sr-only">
                Full Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiUser className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="name"
                  type="text"
                  autoComplete="name"
                  className={`appearance-none rounded-lg relative block w-full px-3 py-3 pl-10 border ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  placeholder="Full Name"
                  {...register('name', {
                    required: 'Name is required',
                    minLength: {
                      value: 2,
                      message: 'Name must be at least 2 characters'
                    }
                  })}
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiMail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className={`appearance-none rounded-lg relative block w-full px-3 py-3 pl-10 border ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  placeholder="Email address"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email address'
                    }
                  })}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className={`appearance-none rounded-lg relative block w-full px-3 py-3 pl-10 pr-10 border ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  placeholder="Password"
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters'
                      },
                      pattern: {
                        value: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/, 
                        message: 'Password must contain letters and numbers'
                      }
                    })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <FiEyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <FiEye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="sr-only">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  className={`appearance-none rounded-lg relative block w-full px-3 py-3 pl-10 border ${
                    errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  placeholder="Confirm Password"
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: value => 
                      value === password || 'Passwords do not match'
                  })}
                />
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          {/* Business details */}
          {userType === 'business' && (
            <div className="space-y-4">
              <div className="pt-2 border-t">
                <h3 className="text-sm font-semibold text-gray-700">Business Details</h3>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Company Name</label>
                <input className="form-input" placeholder="Acme Pvt Ltd" {...register('businessName', { required: 'Company name is required' })} />
                {errors.businessName && <p className="mt-1 text-sm text-red-600">{errors.businessName.message}</p>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Business Email</label>
                  <input
                    className="form-input"
                    type="email"
                    placeholder="billing@company.com"
                    value={watch('businessEmail') || ''}
                    onChange={e => setValue('businessEmail', String(e.target.value || '').toLowerCase())}
                    {...register('businessEmail', {
                      validate: v => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v)) || 'Please enter a valid business email'
                    })}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Business Phone</label>
                  <input
                    className="form-input"
                    placeholder="9876543210"
                    value={watch('businessPhone') || ''}
                    onChange={e => {
                      const raw = String(e.target.value || '');
                      const digits = raw.replace(/\D/g, '').slice(0, 10);
                      setValue('businessPhone', digits);
                    }}
                    {...register('businessPhone', {
                      validate: v => !v || /^\d{10}$/.test(String(v)) || 'Phone must be 10 digits'
                    })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">GSTIN / Tax ID</label>
                  {/* normalize: remove non-alphanumeric, uppercase; client-side validate length 15 */}
                  <input
                    className="form-input"
                    placeholder="27ABCDE1234F1Z5"
                    value={watch('taxId') || ''}
                    onChange={e => {
                      const raw = String(e.target.value || '');
                      const filtered = raw.replace(/[^0-9a-zA-Z]/g, '').toUpperCase().slice(0, 15);
                      setValue('taxId', filtered);
                    }}
                    {...register('taxId', {
                      validate: v => !v || /^[0-9A-Z]{15}$/.test(String(v)) || 'GST must be 15 alphanumeric characters'
                    })}
                  />
                  {errors.taxId && <p className="mt-1 text-sm text-red-600">{errors.taxId.message}</p>}
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Website</label>
                  <input
                    className="form-input"
                    placeholder="https://company.com"
                    value={watch('website') || ''}
                    onChange={e => setValue('website', String(e.target.value || '').trim())}
                    {...register('website', {
                      validate: v => !v || /^https?:\/\/.+/i.test(String(v)) || 'Website must start with http:// or https://'
                    })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Business Address</label>
                <input className="form-input mb-2" placeholder="Street" {...register('businessStreet')} />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input className="form-input" placeholder="City" {...register('businessCity')} />
                  <input className="form-input" placeholder="State" {...register('businessState')} />
                  <input
                    className="form-input"
                    placeholder="PIN / ZIP"
                    value={watch('businessZip') || ''}
                    onChange={e => {
                      const raw = String(e.target.value || '');
                      const digits = raw.replace(/\D/g, '');
                      setValue('businessZip', digits);
                    }}
                    {...register('businessZip', {
                      validate: v => !v || /^\d+$/.test(String(v)) || 'ZIP must contain only numbers'
                    })}
                  />
                </div>
                <input className="form-input mt-2" placeholder="Country" defaultValue="India" {...register('businessCountry')} />
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;